/* eslint-disable @typescript-eslint/no-unused-vars */
import { collection, doc, getDocsFromServer, onSnapshot } from "firebase/firestore";
import {
  type Functions,
  httpsCallable as httpsCallable_,
  type HttpsCallable,
  type HttpsCallableOptions,
} from "firebase/functions";
import { DeleteManyResult } from "react-admin";
import { useMutation, useQuery, useQueryClient, UseQueryResult } from "react-query";
import { z } from "zod";
import { areSetsEqual } from "../backoffice.utils";
import { firestore, functions } from "../firebase";
import { UserToBeDeleted } from "../providers/usersToBeDeletedProvider";
import { StudentStatus } from "../model/StudentStatus";
import { Student, studentsProvider } from "../providers/studentsProvider";
import { t } from "../model/types";
import { AttachmentSchema } from "../model/Attachment";
import { PostalAddress, PostalAddressSchema } from "../model/PostalAddress";
import { CancellationPolicy } from "../model/CancellationPolicy";
import { DateTime } from "luxon";
import { Money } from "../model/Money";
import { BufferTimes } from "../model/BufferTimes";
import { v4 as uuidv4 } from "uuid";
import { isDefined } from "../utils/filters";
import { getAuthenticatedServerClient, schemas } from "./server.api";
import { updateApplied } from "../utils/updateApplied";
import { drivingLessonConverter } from "../providers/converter/drivingLessonConverter";
import { DrivingLessonTypeEnum } from "../model/DrivingLessonType";
import { DateRange } from "../model/DateRange";
import { theoryExamConverter } from "../providers/converter/theoryExamConverter";
import { retryIdempotentOperation } from "../utils/retryIdempotentOperation";
import { OWN_MOTORCYCLE_PSEUDO_ID } from "../model/VehicleType";

function httpsCallable<RequestData = unknown, ResponseData = unknown>(
  functionsInstance: Functions,
  name: string,
  options?: HttpsCallableOptions,
): HttpsCallable<RequestData, ResponseData> {
  if (window.unitTest) {
    return async (requestData: any) => {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw { [`intercepted call to ${name}`]: requestData };
    };
  } else {
    const httpsCallable = httpsCallable_<RequestData, ResponseData>(functionsInstance, name, options);
    return async (requestData: any) => {
      const t0 = Date.now();
      const intervalId = setInterval(() => {
        const dt = Math.round((Date.now() - t0) / 1000);
        console.info(`No response from /${name} after ${dt} s`);
      }, 1000);
      try {
        console.info(`HTTP POST /${name} ...`);
        const response = await httpsCallable(requestData);
        const dt = Date.now() - t0;
        const duration = dt > 5000 ? `${Math.round(dt / 1000)} s` : `${dt} ms`;
        console.info(`HTTP POST /${name} => 200 after ${duration}`);
        return response;
      } catch (error) {
        const dt = Date.now() - t0;
        const duration = dt > 5000 ? `${Math.round(dt / 1000)} s` : `${dt} ms`;
        console.warn(`HTTP POST /${name} failed after ${duration}`);
        throw error;
      } finally {
        clearInterval(intervalId);
      }
    };
  }
}

interface ProviderData {
  /**
   * The user identifier for the linked provider.
   */
  readonly uid: string;
  /**
   * The display name for the linked provider.
   */
  readonly displayName: string;
  /**
   * The email for the linked provider.
   */
  readonly email: string;
  /**
   * The photo URL for the linked provider.
   */
  readonly photoURL: string;
  /**
   * The linked provider ID (for example, "google.com" for the Google provider).
   */
  readonly providerId: string;
  /**
   * The phone number for the linked provider.
   */
  readonly phoneNumber: string;
}

export interface FirebaseAuthUser {
  readonly uid: string;
  readonly displayName?: string;
  readonly disabled: boolean;
  readonly providerData: Array<ProviderData>;
  readonly customClaims?: {
    [key: string]: any;
  };
}

export async function fetchFirebaseAuthUser(uid: string): Promise<FirebaseAuthUser> {
  const getUsers = httpsCallable(functions, "api/backoffice/users");
  const { data } = (await getUsers({ uid })) as { data: Array<FirebaseAuthUser> };
  if (data.length === 0) {
    throw new Error("No data");
  }
  return data[0];
}

export async function setUserRoles(
  userUid: string,
  roles: Array<string>,
  entitledForDrivingSchools?: Array<string>,
): Promise<void> {
  const setUserRoles = httpsCallable<
    {
      userUid: string;
      roles: Array<string>;
      entitledForDrivingSchools?: Array<string>;
    },
    void
  >(functions, "api/backoffice/users/roles");
  await setUserRoles({ userUid, roles, entitledForDrivingSchools });
}

interface SetAvatarOverrideUrlRequest {
  readonly userUid: string;
  readonly avatarOverrideUrl: string;
}

const SetStudentStatusRequestSchema = z.object({
  studentUid: z.string(),
  status: z.enum(["active", "cancelled", "inactive", "onHold", "outstandingPayments"]),
  reason: z.string(),
});

type SetStudentStatusRequest = z.infer<typeof SetStudentStatusRequestSchema>;

export async function setUserAvatarOverride(userUid: string, avatarOverrideUrl: string): Promise<void> {
  const setAvatarOverrideUrl = httpsCallable<SetAvatarOverrideUrlRequest>(
    functions,
    "api/backoffice/users/avatarOverrideUrl",
  );
  await setAvatarOverrideUrl({ userUid, avatarOverrideUrl });
}

export async function setStudentStatus(
  student: Student,
  status: Exclude<StudentStatus, "completed">,
  reason: string,
): Promise<void> {
  const setStudentStatus = httpsCallable<SetStudentStatusRequest>(functions, "api/backoffice/users/set-student-status");
  const updateReceived = new Promise<void>((resolve) => {
    const unsubscribe = studentsProvider.onUpdate(student, (updatedStudent) => {
      if (student.status === "active" && status === "active") {
        if (updatedStudent.verboseStatus === "Aktiv") {
          unsubscribe();
          resolve();
        }
      } else if (updatedStudent.status === status) {
        unsubscribe();
        resolve();
      }
    });
  });
  await setStudentStatus({ studentUid: student.id, status, reason });
  await updateReceived;
}

export async function resetTheoryLearning(student: Student): Promise<void> {
  const resetTheoryLearning = httpsCallable<{ studentUid: string }, void>(
    functions,
    "api/backoffice/users/reset-theory-learning",
  );
  const userDocumentHasCorrectTheoryLearningProgress = new Promise<void>((resolve) => {
    const unsubscribe = onSnapshot(doc(firestore, `users/${student.id}`), (snapshot) => {
      const progress = snapshot.data()?.theoryKPIs?.theoryLearningProgress;
      if (progress?.numCorrectlyAnsweredQuestions === 0 && progress?.numIncorrectlyAnsweredQuestions === 0) {
        unsubscribe();
        resolve();
      }
    });
  });
  await resetTheoryLearning({ studentUid: student.id });
  await userDocumentHasCorrectTheoryLearningProgress;
}

export async function deleteUser(userUid: string): Promise<void> {
  const deleteUser = httpsCallable<{ userUid: string }, void>(functions, "api/backoffice/users/delete");
  await deleteUser({ userUid });
}

export async function deleteUsers(userUids: Array<string>): Promise<DeleteManyResult<UserToBeDeleted>> {
  const deleteUsers = httpsCallable<{ userUids: Array<string> }, Array<string>>(
    functions,
    "api/backoffice/users/delete-many",
  );
  const { data } = await deleteUsers({ userUids });
  return { data };
}

export class MissingAddressDataError extends Error {
  constructor(msg: string) {
    super(msg);
    Object.setPrototypeOf(this, MissingAddressDataError.prototype);
  }
}

const NoteAttachmentUploadSchema = AttachmentSchema.omit({ id: true, size: true, lastChanged: true }).extend({
  size: t.number().int().positive(),
});

const CreateNoteRequestSchema = t.object({
  studentUid: t.uid(),
  body: t.string(),
  attachments: t.optional(t.array(NoteAttachmentUploadSchema)),
});

export async function createNote(data: z.infer<typeof CreateNoteRequestSchema>): Promise<void> {
  const ref = collection(firestore, `users/${data.studentUid}/notes`);
  const numOldNotes = (await getDocsFromServer(ref)).docs.length;
  const newNoteCreated = new Promise<void>((resolve) => {
    const unsubscribe = onSnapshot(ref, (snapshot) => {
      if (snapshot.docs.length > numOldNotes) {
        unsubscribe();
        resolve();
      }
    });
  });
  const createNote = httpsCallable<z.infer<typeof CreateNoteRequestSchema>, void>(
    functions,
    "api/backoffice/notes/create",
  );
  await createNote(data);
  await newNoteCreated;
}

const EditNoteRequestSchema = t.object({
  studentUid: t.uid(),
  noteUid: t.uid(),
  body: t.string(),
  attachments: t.optional(t.array(NoteAttachmentUploadSchema)),
});

export async function editNote(data: z.infer<typeof EditNoteRequestSchema>): Promise<void> {
  const editNote = httpsCallable<z.infer<typeof EditNoteRequestSchema>, void>(functions, "api/backoffice/notes/edit");
  await editNote(data);
}

const SetBranchRequestSchema = t.object({
  studentUid: t.uid(),
  // Might be the empty string "" to unassign a branch.
  branchUid: t.string(),
});

export async function setBranch(student: Student, branchId: string): Promise<void> {
  const setBranch = httpsCallable<z.infer<typeof SetBranchRequestSchema>, void>(
    functions,
    "api/backoffice/users/setBranch",
  );
  const updateReceived = new Promise<void>((resolve) => {
    const unsubscribe = studentsProvider.onUpdate(student, (student) => {
      if (branchId ? student.branchId === branchId : !student.branchId) {
        unsubscribe();
        resolve();
      }
    });
  });
  await setBranch({ studentUid: student.id, branchUid: branchId });
  await updateReceived;
}

export async function assignInstructors(student: Student, instructorUids: Array<string>) {
  const assignInstructors = httpsCallable<{ studentUid: string; instructorUids: Array<string> }, void>(
    functions,
    "api/backoffice/users/assign-instructors",
  );
  const updateReceived = new Promise<void>((resolve) => {
    const unsubscribe = studentsProvider.onUpdate(student, (student) => {
      if (areSetsEqual(new Set(student.instructorIds), new Set(instructorUids))) {
        unsubscribe();
        resolve();
      }
    });
  });
  await assignInstructors({ studentUid: student.id, instructorUids });
  await updateReceived;
}

export const OnlineMeetingSchema = z.object({
  type: z.literal("OnlineMeeting"),
  url: z.string(),
});

export type OnlineMeeting = z.infer<typeof OnlineMeetingSchema>;

export const LocationSchema = PostalAddressSchema.or(OnlineMeetingSchema);

export const GetPriceSuggestionRequestSchema = z.object({
  eventType: t.enum(["DrivingLesson", "RemoteLesson"]),
  drivingLessonType: DrivingLessonTypeEnum.optional(),
  instructorUid: z.string().optional(),
  studentUid: z.string().nonempty(),
  bookedTrainingId: z.string().nonempty(),
  start: z.instanceof(DateTime as any).transform((val) => val.toString()),
  end: z.optional(z.instanceof(DateTime as any).transform((val) => val.toString())),
  location: z.optional(LocationSchema),
});
export type GetPriceSuggestionRequest = z.infer<typeof GetPriceSuggestionRequestSchema>;

const GetPriceSuggestionResponseSchema = z.object({
  data: z.object({
    price: z.object({
      studentPrice: z.object({
        gross: z.number().int().nonnegative(),
      }),
      authorityFees: z.object({
        gross: z.number().int().nonnegative(),
      }),
    }),
  }),
});

export async function getPriceSuggestion(request: GetPriceSuggestionRequest): Promise<{
  price: Money;
  authorityFee: Money;
}> {
  const callable = httpsCallable<GetPriceSuggestionRequest>(functions, "api/backoffice/price-suggestion");
  const { data } = GetPriceSuggestionResponseSchema.parse(
    await retryIdempotentOperation(() => callable(request), { maxRetries: 3, interval: 5000 }),
  );
  return {
    price: { amount: data.price.studentPrice.gross / 100, currency: "EUR" },
    authorityFee: { amount: data.price.authorityFees.gross / 100, currency: "EUR" },
  };
}

export type LessonPriceChange = Pick<
  z.infer<typeof schemas.EditDrivingLessonDto3>,
  "handlePriceDifferenceStrategy" | "manualStudentGross"
>;

export async function editDrivingLesson({
  eventId,
  instructorId,
  start,
  end,
  bufferTimes,
  drivingLessonType,
  bookedTrainingId,
  vehicleId,
  vehicle2Id,
  startLocation,
  remarks,
  handlePriceDifferenceStrategy,
  manualStudentGross,
}: {
  eventId: string;
  instructorId: string;
  start: DateTime;
  end: DateTime;
  bufferTimes: BufferTimes;
  drivingLessonType: z.infer<typeof DrivingLessonTypeEnum>;
  bookedTrainingId: string;
  vehicleId: string | null;
  vehicle2Id: string | null;
  startLocation: PostalAddress;
  remarks?: string;
} & LessonPriceChange): Promise<void> {
  const serverClient = await getAuthenticatedServerClient();
  const payload = {
    type: "DrivingLesson",
    instructorId,
    start: start.toISO(),
    end: end.toISO(),
    bufferTimes,
    drivingLessonType,
    bookedTrainingId,
    vehicleId,
    vehicle2Id,
    location: startLocation,
    ...(remarks ? { remarks } : {}),
    ...(handlePriceDifferenceStrategy ? { handlePriceDifferenceStrategy } : {}),
    ...(manualStudentGross ? { manualStudentGross } : {}),
  } satisfies z.infer<typeof schemas.EditCalendarEventDto3>;
  const updateReceived = new Promise<void>((resolve) => {
    const expected = {
      instructorId,
      start,
      end,
      bufferTimes,
      drivingLessonType,
      startLocation,
      vehicleId: vehicleId === OWN_MOTORCYCLE_PSEUDO_ID ? null : vehicleId,
      vehicle2Id,
      studentUsesOwnMotorcycle: vehicleId === OWN_MOTORCYCLE_PSEUDO_ID,
      student: { bookedTrainingId },
      remarks,
    };
    const unsubscribe = onSnapshot(doc(firestore, `calendar_events/${eventId}`), (snapshot) => {
      const updatedDrivingLesson = drivingLessonConverter.fromFirestore(snapshot);
      if (updateApplied(expected, updatedDrivingLesson)) {
        unsubscribe();
        resolve();
      }
    });
  });
  await serverClient.editEvent_v3(payload, { params: { eventId } });
  await updateReceived;
}

export async function editSimulatorDrivingLesson({
  eventId,
  start,
  end,
  bookedTrainingId,
  remarks,
}: {
  eventId: string;
  start: DateTime;
  end: DateTime;
  bookedTrainingId: string;
  remarks?: string;
}): Promise<void> {
  const serverClient = await getAuthenticatedServerClient();
  const payload = {
    type: "DrivingLesson",
    start: start.toISO(),
    end: end.toISO(),
    bookedTrainingId,
    ...(remarks ? { remarks } : {}),
  } satisfies z.infer<typeof schemas.EditCalendarEventDto3>;
  const updateReceived = new Promise<void>((resolve) => {
    const expected = {
      start,
      end,
      student: { bookedTrainingId },
      ...(remarks ? { remarks } : {}),
    };
    const unsubscribe = onSnapshot(doc(firestore, `calendar_events/${eventId}`), (snapshot) => {
      const updatedDrivingLesson = drivingLessonConverter.fromFirestore(snapshot);
      if (updateApplied(expected, updatedDrivingLesson)) {
        unsubscribe();
        resolve();
      }
    });
  });
  await serverClient.editEvent_v3(payload, { params: { eventId } });
  await updateReceived;
}

export async function editTheoryExam({
  eventId,
  start,
  location,
  remarks,
}: {
  eventId: string;
  start: DateTime;
  location: PostalAddress;
  remarks?: string;
}): Promise<void> {
  const serverClient = await getAuthenticatedServerClient();
  const end = start.plus({ minutes: 45 });
  const payload = {
    type: "DrivingLesson", // ... this is correct, a TheoryExam is a DrivingLesson in the backend.
    start: start.toISO(),
    end: end.toISO(),
    location,
    ...(remarks ? { remarks } : {}),
  } satisfies z.infer<typeof schemas.EditCalendarEventDto3>;
  const updateReceived = new Promise<void>((resolve) => {
    const expected = {
      start,
      end,
      startLocation: location,
      remarks,
    };
    const unsubscribe = onSnapshot(doc(firestore, `calendar_events/${eventId}`), (snapshot) => {
      const updatedTheoryExam = theoryExamConverter.fromFirestore(snapshot);
      if (updateApplied(expected, updatedTheoryExam)) {
        unsubscribe();
        resolve();
      }
    });
  });
  await serverClient.editEvent_v3(payload, { params: { eventId } });
  await updateReceived;
}

const SetReadyForTheoryExamRequestSchema = z.object({
  studentUid: t.uid(),
  isReady: t.boolean(),
});

export function useSetReadyForTheoryExamMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation(setReadyForTheoryExam, {
    onSuccess: async () => {
      void queryClient.invalidateQueries(["students"]);
    },
  });
  return mutation;
}

async function setReadyForTheoryExam(data: z.infer<typeof SetReadyForTheoryExamRequestSchema>): Promise<void> {
  const studentUpdateReceived = new Promise<void>((resolve) => {
    const unsubscribe = onSnapshot(doc(firestore, `users/${data.studentUid}`), (snapshot) => {
      if (snapshot.data()?.isReadyForTheoryExam === data.isReady) {
        unsubscribe();
        resolve();
      }
    });
  });
  const callable = httpsCallable<z.infer<typeof SetReadyForTheoryExamRequestSchema>, void>(
    functions,
    "api/backoffice/users/set-ready-for-theory-exam",
  );
  await callable(data);
  await studentUpdateReceived;
}

const AddStudentToTheoryLessonRequestSchema = t.object({
  calendarEventUid: t.uid(),
  studentUid: t.uid(),
});

async function addStudentToTheoryLesson(request: z.infer<typeof AddStudentToTheoryLessonRequestSchema>): Promise<void> {
  const studentWasAdded = new Promise<void>((resolve) => {
    const unsubscribe = onSnapshot(doc(firestore, `calendar_events/${request.calendarEventUid}`), (snapshot) => {
      const data = snapshot.data();
      if (data?.students?.[request.studentUid]) {
        unsubscribe();
        resolve();
      }
    });
  });
  const callable = httpsCallable<z.infer<typeof AddStudentToTheoryLessonRequestSchema>, void>(
    functions,
    "api/backoffice/event/add-student-to-theory-lesson",
  );
  await callable(request);
  await studentWasAdded;
}

export function useAddStudentToTheoryLessonMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation(addStudentToTheoryLesson, { onSuccess: () => queryClient.invalidateQueries() });
  return mutation;
}

const RemoveStudentsFromTheoryLessonRequestSchema = t.object({
  calendarEventUid: t.uid(),
  studentUids: t.array(t.uid()),
});

async function removeStudentsFromTheoryLesson(
  request: z.infer<typeof RemoveStudentsFromTheoryLessonRequestSchema>,
): Promise<void> {
  const studentsHaveBeenRemoved = new Promise<void>((resolve) => {
    const unsubscribe = onSnapshot(doc(firestore, `calendar_events/${request.calendarEventUid}`), (snapshot) => {
      const data = snapshot.data();
      if (Object.keys(data?.students ?? {}).every((uid) => !request.studentUids.includes(uid))) {
        unsubscribe();
        resolve();
      }
    });
  });
  const callable = httpsCallable<z.infer<typeof RemoveStudentsFromTheoryLessonRequestSchema>, void>(
    functions,
    "api/backoffice/event/remove-students-from-theory-lesson",
  );
  await callable(request);
  await studentsHaveBeenRemoved;
}

export function useRemoveStudentsFromTheoryLessonMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation(removeStudentsFromTheoryLesson, { onSuccess: () => queryClient.invalidateQueries() });
  return mutation;
}

const DeleteAutovioCalendarEventRequestSchema = t.object({
  calendarEventUid: t.string().nonempty(),
  reason: t.optional(t.string().min(5)),
});

export function useDeleteEventMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation(deleteEvent, { onSuccess: () => queryClient.invalidateQueries() });
  return mutation;
}

export async function deleteEvent({
  calendarEventUid,
  reason,
}: {
  calendarEventUid: string;
  reason?: string;
}): Promise<void> {
  const calendarEventIsMarkedAsDeleted = new Promise<void>((resolve) => {
    const unsubscribe = onSnapshot(doc(firestore, `calendar_events/${calendarEventUid}`), (snapshot) => {
      const data = snapshot.data();
      if (data?.deleted) {
        unsubscribe();
        resolve();
      }
    });
  });
  const callable = httpsCallable<z.infer<typeof DeleteAutovioCalendarEventRequestSchema>, void>(
    functions,
    "api/backoffice/event/delete",
  );
  await callable({ calendarEventUid, ...(reason ? { reason } : {}) });
  await calendarEventIsMarkedAsDeleted;
}

const ResetPasswordRequestSchema = t.object({
  userUid: t.uid(),
  newPassword: t.string(),
});

export async function resetPassword(params: { userUid: string; newPassword: string }): Promise<void> {
  const callable = httpsCallable<z.infer<typeof ResetPasswordRequestSchema>, void>(
    functions,
    "api/backoffice/users/reset-password",
  );
  await callable(params);
}

export async function bookRecommendation({
  recommendation,
  startLocation,
  studentRsvp,
  studentAgreedCancellationPolicy,
}: {
  recommendation: Recommendation;
  startLocation: PostalAddress;
  studentRsvp: "pending" | "accepted";
  studentAgreedCancellationPolicy?: CancellationPolicy;
}): Promise<void> {
  const callable = httpsCallable<BookRecommendationRequest>(functions, "api/backoffice/recommendations/book");
  const request: BookRecommendationRequest = {
    uid: uuidv4(),
    start: recommendation.start.setZone("Europe/Berlin").toISO(),
    end: recommendation.end.setZone("Europe/Berlin").toISO(),
    resourceUids: recommendation.resourceUids,
    drivingLessonType: recommendation.drivingLessonType,
    startLocation,
    instructorUid: recommendation.instructorUid,
    studentUid: recommendation.studentUid,
    studentRsvp,
    bookedTrainingUid: recommendation.bookedTrainingId,
    ...(studentAgreedCancellationPolicy ? { studentAgreedCancellationPolicy } : {}),
  };
  await callable(request);
}

export const useBookRecommendationMutation = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation(bookRecommendation, { onSuccess: () => queryClient.invalidateQueries() });
  return mutation;
};

export type RecommendationsData = {
  recommendations: RecommendationWithPrice[];
  removedRecommendations: Recommendation[];
  noResultReasonPerDay: { [isoDay: string]: NoRecommendationResultReason | undefined };
};
export const useStudentRecommendationsQuery = (
  props: UseStudentRecommendationsQueryProps | undefined,
): UseQueryResult<RecommendationsData> => {
  const callable = httpsCallable<StudentRecommendationRangeQuery, RecommendationsInRangeDto>(
    functions,
    `api/backoffice/recommendations/student/range`,
  );
  return useQuery({
    queryKey: _studentRecommendationsQueryKey(props),
    queryFn: () => fetchRecommendations(callable, props),
    enabled: !!props?.startLocation,
  });
};

const _studentRecommendationsQueryKey = (props: undefined | UseStudentRecommendationsQueryProps) => [
  "recommendations",
  "student",
  {
    studentUid: props?.student?.id,
    instructorId: props?.instructorId,
    bookedTrainingId: props?.bookedTrainingId,
    startLocation: props?.startLocation,
  },
  { dataRange: `${props?.dateRange[0].toString()} - ${props?.dateRange[1].toString()}` },
  { options: props?.options },
];

const fetchRecommendations = async (
  callable: HttpsCallable<StudentRecommendationRangeQuery, RecommendationsInRangeDto>,
  props?: UseStudentRecommendationsQueryProps,
) => {
  if (!props?.startLocation) {
    throw new Error("Unknown startLocation");
  }
  const { data } = await callable({
    start: props.dateRange[0].toDateTime().toISO(),
    end: props.dateRange[1].nextDay.toDateTime().toISO(),
    bookedTrainingId: props.bookedTrainingId,
    instructorUid: props.instructorId,
    studentUid: props.student.id,
    startLocation: props.startLocation,
    options: props.options,
  });
  const recommendationsPerDay = RecommendationsInRangeDtoSchema.parse(data);
  const noResultReasonPerDay: { [isoDay: string]: NoRecommendationResultReason | undefined } = Object.fromEntries(
    Object.entries(recommendationsPerDay).map(([day, data]) => [day, data.noResultReason]),
  );
  const recommendations = Object.values(recommendationsPerDay).flatMap((it) =>
    it.recommendations.map((it) => RecommendationWithPriceSchema.parse(it)),
  );
  const removedRecommendations = Object.values(recommendationsPerDay)
    .flatMap((it) => it.removedRecommendations?.map((it) => RecommendationSchema.parse(it)))
    .filter(isDefined);
  return { recommendations, removedRecommendations, noResultReasonPerDay };
};

const BookableDrivingLessonTypeForRecommendationsSchema = z.enum(["normal"]);
const BookRecommendationRequestSchema = z.object({
  uid: z.string(),
  studentUid: z.string(),
  studentRsvp: z.union([z.literal("pending"), z.literal("accepted")]),
  bookedTrainingUid: z.string(),
  instructorUid: z.string(),
  start: z.string(),
  end: z.string(),
  drivingLessonType: BookableDrivingLessonTypeForRecommendationsSchema,
  startLocation: PostalAddressSchema,
  resourceUids: z.string().array(),
});
type BookRecommendationRequest = z.infer<typeof BookRecommendationRequestSchema>;

const StudentRecommendationRangeQuerySchema = z.object({
  studentUid: z.string(),
  instructorUid: z.string(),
  bookedTrainingId: z.string(),
  startLocation: PostalAddressSchema,
  start: z.string(),
  end: z.string(),
  options: z
    .object({
      returnRemoved: z.boolean().optional(),
      ignoreMaxDaysInAdvance: z.boolean().optional(),
      ignoreMaxDrivingLessonsPerWeek: z.boolean().optional(),
    })
    .optional(),
});

const RemoveReasonSchema = z.enum([
  "minTimeAhead",
  "maxDrivingLessonLUsPerDay",
  "maxDrivingLessonsPerWeek",
  "maxWorkTimePerDay",
  "maxBookableDrivingLessonsPerWeek",
  "maxDaysInAdvance",
  "pickBestRecommendations",
]);

const NoRecommendationResultReasonSchema = RemoveReasonSchema.or(
  z.enum(["noQueries", "isHoliday", "notWorkingToday", "unknown"]),
);
export type NoRecommendationResultReason = z.infer<typeof NoRecommendationResultReasonSchema>;

const RecommendationDtoSchema = z.object({
  instructorUid: z.string(),
  studentUid: z.string(),
  bookedTrainingId: z.string(),
  drivingLessonType: BookableDrivingLessonTypeForRecommendationsSchema,
  resourceUids: z.string().array(),
  score: z.number().optional(),
  individualScores: z
    .object({
      coverage: z.number(),
      duration: z.number(),
      startHour: z.number(),
      instructorSwitch: z.number(),
      resourceSwitch: z.number(),
      preferredResource: z.number(),
    })
    .optional(),
  start: z.string(),
  end: z.string(),
  removeReason: RemoveReasonSchema.optional(),
});
type RecommendationDto = z.infer<typeof RecommendationDtoSchema>;

const PriceCompositionSchema = z.object({
  gross: z.number(),
  net: z.number(),
  vat: z.number(),
});
const PriceSchema = z.object({
  studentPrice: PriceCompositionSchema,
  instructorPrice: PriceCompositionSchema.extend({ fee: z.number() }),
  authorityFees: PriceCompositionSchema,
});
const RecommendationWithPriceDtoSchema = RecommendationDtoSchema.extend({ price: PriceSchema });
export const RecommendationsInRangeDtoSchema = z.record(
  z.string(),
  z.object({
    recommendations: RecommendationWithPriceDtoSchema.array(),
    removedRecommendations: RecommendationDtoSchema.array().optional(),
    isHoliday: z.boolean().optional(),
    noResultReason: NoRecommendationResultReasonSchema.optional(),
  }),
);

export const transformRecommendation = <R extends RecommendationDto>(val: R) => ({
  ...val,
  id: `${val.instructorUid}-${val.studentUid}-${val.bookedTrainingId}--${val.drivingLessonType}-${val.start}-${val.end}`,
  start: DateTime.fromISO(val.start),
  end: DateTime.fromISO(val.end),
});

const RecommendationSchema = RecommendationDtoSchema.transform(transformRecommendation);
const RecommendationWithPriceSchema = RecommendationWithPriceDtoSchema.transform(transformRecommendation);
export type Recommendation = z.infer<typeof RecommendationSchema>;
export type RecommendationWithPrice = z.infer<typeof RecommendationWithPriceSchema>;
type StudentRecommendationRangeQuery = z.infer<typeof StudentRecommendationRangeQuerySchema>;
type RecommendationsInRangeDto = z.infer<typeof RecommendationsInRangeDtoSchema>;

interface UseStudentRecommendationsQueryProps {
  readonly dateRange: DateRange;
  readonly student: Student;
  readonly startLocation?: PostalAddress;
  readonly bookedTrainingId: string;
  readonly instructorId: string;
  readonly options?: {
    readonly returnRemoved?: boolean;
    readonly ignoreMaxDaysInAdvance?: boolean;
    readonly ignoreMaxBookableDrivingLessonsPerWeek?: boolean;
  };
}
